import Dexie, { type EntityTable, type Transaction } from 'dexie';
import {
  INBOX_LIST_ID,
  type Attachment,
  type Blob_,
  type Conflict,
  type GlobalTag,
  type Item,
  type List,
  type ListTag,
  type Meta,
} from './types';

/**
 * Dexie is what the UI reads and writes — always, never the network.
 * A sync engine reconciles with the server in the background; nothing in the
 * UI layer knows or cares whether that engine exists.
 */
class SlippingNotesDB extends Dexie {
  items!: EntityTable<Item, 'id'>;
  lists!: EntityTable<List, 'id'>;
  listTags!: EntityTable<ListTag, 'id'>;
  globalTags!: EntityTable<GlobalTag, 'id'>;
  attachments!: EntityTable<Attachment, 'id'>;
  blobs!: EntityTable<Blob_, 'attachment_id'>;
  meta!: EntityTable<Meta, 'key'>;
  conflicts!: EntityTable<Conflict, 'id'>;

  constructor() {
    super('slippingnotes');

    this.version(1).stores({
      items: 'id, kind, done, due_at, start_at, list_id, *tags, updated_at, dirty, deleted',
      lists: 'id, updated_at, dirty, deleted',
      attachments: 'id, item_id, updated_at, dirty, deleted, uploaded',
      blobs: 'attachment_id',
      meta: 'key',
      conflicts: '++id, item_id, at',
    });

    this.version(2).stores({
      tags: 'id, &path, list_id, updated_at, dirty, deleted',
    });

    // v3 splits tagging in two: tags scoped to a list, and tags that cut across
    // every list. The old nested-path strings become global tags — they were
    // list-agnostic in practice, so that is the migration that loses nothing.
    this.version(3)
      .stores({
        items:
          'id, kind, done, due_at, start_at, list_id, *list_tag_ids, *global_tag_ids, updated_at, dirty, deleted',
        listTags: 'id, list_id, updated_at, dirty, deleted',
        globalTags: 'id, updated_at, dirty, deleted',
        tags: null, // superseded by listTags + globalTags
      })
      .upgrade(migrateToHybridTags);

    // v4 makes the Trash retention real. It needs to know when something was
    // deleted, which nothing recorded before now.
    this.version(4)
      .stores({
        items:
          'id, kind, done, due_at, start_at, list_id, *list_tag_ids, *global_tag_ids, updated_at, deleted_at, dirty, deleted',
      })
      .upgrade(migrateDeletedAt);
  }
}

export const db = new SlippingNotesDB();

export const uid = () => crypto.randomUUID();
export const now = () => new Date().toISOString();

/** Every local write marks the row dirty and bumps updated_at. */
function stamp<T extends { updated_at: string; dirty: 0 | 1 }>(patch: Partial<T>) {
  return { ...patch, updated_at: now(), dirty: 1 as const };
}

/* ── Migration ───────────────────────────────────────────── */

interface LegacyItem extends Omit<Item, 'list_id' | 'list_tag_ids' | 'global_tag_ids'> {
  list_id: string | null;
  tags?: string[];
}

interface LegacyTag {
  path: string;
  color: string | null;
  description?: string;
}

async function migrateToHybridTags(tx: Transaction) {
  const items = tx.table<LegacyItem>('items');
  const lists = tx.table<List>('lists');
  const globalTags = tx.table<GlobalTag>('globalTags');

  // Colours and descriptions from the old tag table, so nothing set by hand is
  // thrown away by the restructure.
  let legacy: LegacyTag[] = [];
  try {
    legacy = await tx.table<LegacyTag>('tags').toArray();
  } catch {
    // v1 databases never had the table; there is simply nothing to carry over.
  }
  const legacyByPath = new Map(legacy.map((t) => [t.path, t]));

  await lists.put(inboxList());

  const rows = await items.toArray();
  const created = new Map<string, string>();
  let order = Date.now();

  for (const row of rows) {
    const paths = row.tags ?? [];
    const globalIds: string[] = [];

    for (const path of paths) {
      let id = created.get(path);
      if (!id) {
        id = uid();
        created.set(path, id);
        const meta = legacyByPath.get(path);
        await globalTags.put({
          id,
          // Flat now: a slash is no longer structure, so it stays in the name
          // rather than being silently rewritten into something else.
          name: path,
          color: meta?.color ?? null,
          sort_order: order++,
          deleted: false,
          dirty: 1,
          created_at: now(),
          updated_at: now(),
        });
      }
      globalIds.push(id);
    }

    // Destructured out, not set to `undefined`: a key holding `undefined` is
    // still an own key, and the sync engine's payload builder counts keys
    // rather than values. Leaving the ghost behind is what makes the retired
    // `tags` column reappear in an insert with nothing to put in it.
    const { tags: _retired, ...rest } = row;

    await items.put({
      ...rest,
      list_id: row.list_id ?? INBOX_LIST_ID,
      list_tag_ids: [],
      global_tag_ids: globalIds,
      updated_at: now(),
      dirty: 1,
    } as LegacyItem);
  }
}

/**
 * Backfills `deleted_at` on a database that predates it.
 *
 * Anything already in the Trash gets `updated_at` as its best available
 * deletion time — it is the moment the tombstone was written unless the row was
 * touched afterwards, and erring towards "deleted recently" is the safe
 * direction: it grants the item a full retention window rather than making the
 * first sweep after the upgrade purge things without warning.
 */
async function migrateDeletedAt(tx: Transaction) {
  await tx
    .table<Item>('items')
    .toCollection()
    .modify((item) => {
      item.deleted_at = item.deleted ? item.updated_at : null;
    });
}

const inboxList = (): List => ({
  id: INBOX_LIST_ID,
  name: 'Inbox',
  sort_order: 0,
  color: null,
  system: true,
  deleted: false,
  dirty: 1,
  created_at: now(),
  updated_at: now(),
});

/**
 * Guarantees the Inbox exists before anything reads it. Idempotent by design —
 * the id is fixed, so running this on two devices converges instead of
 * producing two Inboxes.
 */
export async function ensureDefaults(): Promise<void> {
  const existing = await db.lists.get(INBOX_LIST_ID);
  if (existing) return;
  await db.lists.put(inboxList());
}

/* ── Items ───────────────────────────────────────────────── */

export function newItem(patch: Partial<Item> = {}): Item {
  return {
    id: uid(),
    kind: 'todo',
    title: '',
    body: '',
    done: false,
    completed_at: null,
    due_at: null,
    start_at: null,
    remind_at: null,
    priority: 4,
    list_id: INBOX_LIST_ID,
    list_tag_ids: [],
    global_tag_ids: [],
    sort_order: Date.now(),
    source_note_id: null,
    deleted: false,
    deleted_at: null,
    dirty: 1,
    created_at: now(),
    updated_at: now(),
    ...patch,
  };
}

export const createItem = async (patch: Partial<Item> = {}) => {
  const item = newItem(patch);
  await db.items.add(item);
  return item;
};

export const updateItem = (id: string, patch: Partial<Item>) => db.items.update(id, stamp(patch));

export const toggleDone = (item: Item) =>
  updateItem(item.id, { done: !item.done, completed_at: item.done ? null : now() });

/**
 * Never hard-delete here. The tombstone is how the other device learns it is
 * gone; `deleted_at` starts the retention clock that eventually purges it.
 */
export const softDelete = (id: string) => updateItem(id, { deleted: true, deleted_at: now() });
export const restore = (id: string) => updateItem(id, { deleted: false, deleted_at: null });

/**
 * Moves an item to another list.
 *
 * List tags are dropped, because they do not exist outside the list that owns
 * them — carrying the ids across would leave the item pointing at tags the new
 * list has never heard of. Global tags are untouched: being independent of any
 * list is the entire reason they exist.
 */
export async function moveToList(itemId: string, listId: string): Promise<void> {
  await updateItem(itemId, { list_id: listId, list_tag_ids: [] });
}

/* ── Lists ───────────────────────────────────────────────── */

export function newList(patch: Partial<List> & { name: string }): List {
  return {
    id: uid(),
    sort_order: Date.now(),
    color: null,
    system: false,
    deleted: false,
    dirty: 1,
    created_at: now(),
    updated_at: now(),
    ...patch,
  };
}

export const createList = async (patch: Partial<List> & { name: string }) => {
  const list = newList(patch);
  await db.lists.add(list);
  return list;
};

export const updateList = (id: string, patch: Partial<List>) => db.lists.update(id, stamp(patch));

export async function findOrCreateList(name: string, color: string | null = null): Promise<List> {
  const trimmed = name.trim();
  const existing = (await db.lists.toArray()).find(
    (l) => !l.deleted && l.name.toLowerCase() === trimmed.toLowerCase(),
  );
  return existing ?? createList({ name: trimmed, color });
}

/**
 * Deleting a list takes its list tags with it and rehomes its items to the
 * Inbox. Nothing is orphaned, and nothing is destroyed — every item survives,
 * it just moves somewhere that still exists.
 */
export async function deleteList(listId: string): Promise<number> {
  if (listId === INBOX_LIST_ID) return 0;

  return db.transaction('rw', db.lists, db.listTags, db.items, async () => {
    const orphans = await db.items.where('list_id').equals(listId).toArray();
    for (const item of orphans) {
      await db.items.update(item.id, stamp<Item>({ list_id: INBOX_LIST_ID, list_tag_ids: [] }));
    }
    const tags = await db.listTags.where('list_id').equals(listId).toArray();
    for (const tag of tags) {
      await db.listTags.update(tag.id, stamp<ListTag>({ deleted: true }));
    }
    await db.lists.update(listId, stamp<List>({ deleted: true }));
    return orphans.length;
  });
}

/* ── List tags ───────────────────────────────────────────── */

export const createListTag = async (patch: Partial<ListTag> & { list_id: string; name: string }) => {
  const tag: ListTag = {
    id: uid(),
    color: null,
    sort_order: Date.now(),
    deleted: false,
    dirty: 1,
    created_at: now(),
    updated_at: now(),
    ...patch,
  };
  await db.listTags.add(tag);
  return tag;
};

export const updateListTag = (id: string, patch: Partial<ListTag>) =>
  db.listTags.update(id, stamp(patch));

/** Removes the tag and strips its id from every item that carried it. */
export async function deleteListTag(tagId: string): Promise<void> {
  await db.transaction('rw', db.listTags, db.items, async () => {
    const tagged = await db.items.where('list_tag_ids').equals(tagId).toArray();
    for (const item of tagged) {
      await db.items.update(
        item.id,
        stamp<Item>({ list_tag_ids: item.list_tag_ids.filter((id) => id !== tagId) }),
      );
    }
    await db.listTags.update(tagId, stamp<ListTag>({ deleted: true }));
  });
}

/* ── Global tags ─────────────────────────────────────────── */

export const createGlobalTag = async (patch: Partial<GlobalTag> & { name: string }) => {
  const tag: GlobalTag = {
    id: uid(),
    color: null,
    sort_order: Date.now(),
    deleted: false,
    dirty: 1,
    created_at: now(),
    updated_at: now(),
    ...patch,
  };
  await db.globalTags.add(tag);
  return tag;
};

export const updateGlobalTag = (id: string, patch: Partial<GlobalTag>) =>
  db.globalTags.update(id, stamp(patch));

export async function deleteGlobalTag(tagId: string): Promise<void> {
  await db.transaction('rw', db.globalTags, db.items, async () => {
    const tagged = await db.items.where('global_tag_ids').equals(tagId).toArray();
    for (const item of tagged) {
      await db.items.update(
        item.id,
        stamp<Item>({ global_tag_ids: item.global_tag_ids.filter((id) => id !== tagId) }),
      );
    }
    await db.globalTags.update(tagId, stamp<GlobalTag>({ deleted: true }));
  });
}

export async function findOrCreateGlobalTag(name: string): Promise<GlobalTag> {
  const trimmed = name.trim().replace(/^[#@]/, '');
  const existing = (await db.globalTags.toArray()).find(
    (t) => !t.deleted && t.name.toLowerCase() === trimmed.toLowerCase(),
  );
  return existing ?? createGlobalTag({ name: trimmed });
}

/* ── Ordering ────────────────────────────────────────────── */

/**
 * Sort key for dropping a row between two others: average the neighbours.
 * Float precision runs out after ~50 consecutive inserts into the same gap —
 * renumber the list when that happens.
 */
export function sortKeyBetween(before: number | undefined, after: number | undefined) {
  if (before === undefined && after === undefined) return Date.now();
  if (before === undefined) return after! - 1;
  if (after === undefined) return before + 1;
  return (before + after) / 2;
}

/** Moves `id` to sit at `index` within an ordered collection. */
export async function reorder<T extends { id: string; sort_order: number }>(
  table: EntityTable<T, 'id'>,
  ordered: T[],
  id: string,
  index: number,
): Promise<void> {
  const without = ordered.filter((row) => row.id !== id);
  const at = Math.max(0, Math.min(without.length, index));
  const sort_order = sortKeyBetween(without[at - 1]?.sort_order, without[at]?.sort_order);
  // Dexie's key type is generic over the table; every table here is keyed by a
  // string id, which the generic signature cannot express on its own.
  await (table as unknown as { update(key: string, changes: object): Promise<number> }).update(id, {
    sort_order,
    updated_at: now(),
    dirty: 1,
  });
}
